import { observer } from 'mobx-react';
import React, { Fragment, useEffect, useState } from 'react';
import { ISpecificationCategoryModel } from '../../../../../../service/specification/category/ISpecificationCategoryModel';
import { ISpecificationEntityModel } from '../../../../../../service/specification/entity/ISpecificationEntityModel';
import { FormMultiAutocomplete } from '../../../../../design/form/FormMultiAutocomplete';
import { SpecificationFormSelectionDomain } from './SpecificationFormSelectionDomain';
import { SpecificationCategoryType } from '../../../../../../service/specification/category/ISpecificationCategoryModelConditionMap';
import { FormTextArea } from '../../../../../design/form/FormTextArea';
import { ApplicationDetailDomain } from '../../../../../user/page/application/info/store/ApplicationDetailDomain';
import { FormAutocomplete } from '../../../../../design/form/FormAutocomplete';

export interface SpecificationFormSelectionProperties {
  selectedSpecificationsIds: string[];
  specificationCategories: ISpecificationCategoryModel[];
  specifications: ISpecificationEntityModel[];
  onChange: (specificationIds: string[]) => void;
  disabled?: boolean;
  isShowAllConditionCategories?: boolean;
  componentFromType: 'fromApplicationCreate' | 'fromRequirements';
  applicationDetailDomain?: ApplicationDetailDomain
}

export const SpecificationFormSelection = observer((props: SpecificationFormSelectionProperties) => {
  const [domain] = useState<SpecificationFormSelectionDomain>(new SpecificationFormSelectionDomain());
  const applicationDetailDomain = props.applicationDetailDomain || null;

  useEffect(() => {
    domain.onLoad({
      selectedSpecificationsIds: props.selectedSpecificationsIds,
      specifications: props.specifications,
      specificationCategories: props.specificationCategories
    })
  }, [props.selectedSpecificationsIds, props.specifications, props.specificationCategories])

  return (
    <Fragment>
      {props.specificationCategories.map((specificationCategory) => {
        if (applicationDetailDomain !== null
          && props.componentFromType === 'fromApplicationCreate'
          && specificationCategory.categoryType === SpecificationCategoryType.specificationCategoryText) {
          const currentSavedField = applicationDetailDomain.ui.specificationsTextFieldsValues.list.find(value => value.categoryId === specificationCategory.id)
          const textFieldName = specificationCategory.isActive ? specificationCategory.name : specificationCategory.name + ' (неактивен)'
          const currentValue = currentSavedField ? currentSavedField.name : ''
          const isHaveError =
            applicationDetailDomain.ui.specificationsTextFieldsErrors.value.find(
              errorId => errorId === specificationCategory.id
            ) || null

          return (specificationCategory.isActive ?
            <FormTextArea
              value={currentValue || ''}
              onChange={(newValue) => {
                applicationDetailDomain.onEditCategoryTextField({ category: specificationCategory, newValue })
              }}
              label={textFieldName || ''}
              required={specificationCategory.conditionsTextMap?.isRequired}
              paddingSizing={1}
              size={'small'}
              autoRows={true}
              minRows={2}
              errorMessage={isHaveError ? 'Обязательно к заполнению' : null}
            /> : currentValue ? <FormTextArea
              value={currentValue || ''}
              onChange={(newValue) => {
                applicationDetailDomain.onEditCategoryTextField({ category: specificationCategory, newValue })
              }}
              label={specificationCategory.name + ' (неактивен)' || ''}
              required={specificationCategory.conditionsTextMap?.isRequired}
              paddingSizing={1}
              size={'small'}
              rows='2'
              errorMessage={isHaveError ? 'Обязательно к заполнению' : null}
            /> : <></>
          )
        } else {
          return (
            <SpecificationItem
              {...props}
              domain={domain}
              specificationCategory={specificationCategory}
              key={specificationCategory.id}
              type={props.componentFromType}
            />
          )
        }

      })}
    </Fragment>
  );
});

export const SpecificationItem = observer(
  ({
    selectedSpecificationsIds,
    specificationCategories,
    specifications,
    onChange,
    disabled = false,
    domain,
    specificationCategory,
    isShowAllConditionCategories = false,
    type,
  }: {
    selectedSpecificationsIds: string[],
    specificationCategories?,
    specifications: any,
    onChange: any,
    disabled?: boolean,
    domain: SpecificationFormSelectionDomain,
    specificationCategory: ISpecificationCategoryModel,
    isShowAllConditionCategories?: boolean,
    type: 'fromApplicationCreate' | 'fromRequirements';
  }) => {
    let isIncludeValueForCategoryTrueCondition: boolean | undefined;
    let isIncludeValueForCategoryFalseCondition: boolean | undefined;
    let conditionRenderStatus: boolean | undefined;
    let categorySelectedValues: string[] = [];
    let categorySelectedValue = '';

    const categoryValues = domain.specificationCategoriesValues(specifications, specificationCategory.id);
    const isNeedMultipleSelect = !specificationCategory.isSingleChoice || type === 'fromRequirements'

    if (isNeedMultipleSelect) {
      categorySelectedValues = selectedSpecificationsIds.filter((selectedSpecificationId) =>
        categoryValues.find((categoryValue) => categoryValue.value === selectedSpecificationId),
      );

      isIncludeValueForCategoryTrueCondition =
        (specificationCategory.conditionsMap?.useIfSpecificationEntitiesIds?.length || 0) > 0
          ? specificationCategory.conditionsMap?.useIfSpecificationEntitiesIds?.some((specificationId) =>
            selectedSpecificationsIds.includes(specificationId),
          )
          : true;

      isIncludeValueForCategoryFalseCondition =
        (specificationCategory.conditionsMap?.useIfNotSpecificationEntitiesIds?.length || 0) > 0
          ? specificationCategory.conditionsMap?.useIfNotSpecificationEntitiesIds?.some((specificationId) =>
            selectedSpecificationsIds.includes(specificationId),
          )
          : false;

      conditionRenderStatus = isIncludeValueForCategoryTrueCondition && !isIncludeValueForCategoryFalseCondition;
    } else {
      categorySelectedValues = selectedSpecificationsIds.filter((selectedSpecificationId) =>
        categoryValues.find((categoryValue) => categoryValue.value === selectedSpecificationId),
      );
      isIncludeValueForCategoryTrueCondition =
        (specificationCategory.conditionsMap?.useIfSpecificationEntitiesIds?.length || 0) > 0
          ? specificationCategory.conditionsMap?.useIfSpecificationEntitiesIds?.some((specificationId) =>
            selectedSpecificationsIds.includes(specificationId),
          )
          : true;

      isIncludeValueForCategoryFalseCondition =
        (specificationCategory.conditionsMap?.useIfNotSpecificationEntitiesIds?.length || 0) > 0
          ? specificationCategory.conditionsMap?.useIfNotSpecificationEntitiesIds?.some((specificationId) =>
            selectedSpecificationsIds.includes(specificationId),
          )
          : false;
      conditionRenderStatus = isIncludeValueForCategoryTrueCondition && !isIncludeValueForCategoryFalseCondition;
      categorySelectedValue = categorySelectedValues[0];
    }

    const isNeedToRender =
      (isShowAllConditionCategories || conditionRenderStatus) &&
      categoryValues.length > 0 &&
      (specificationCategory.isActive || categorySelectedValues.length > 0)
      && specificationCategory.categoryType === SpecificationCategoryType.specificationCategoryValue;

    if (!isNeedToRender) {
      const indexToDelete: number[] = [];
      selectedSpecificationsIds.forEach((selectedSpec, iterator) => {
        if (categorySelectedValues.includes(selectedSpec)) {
          indexToDelete.push(iterator);
        }
      })
      for (let i = indexToDelete.length - 1; i >= 0; i--) {
        selectedSpecificationsIds.splice(indexToDelete[i], 1);
      }

      categorySelectedValues = [];
    }
    const isHaveMoreValuesThanNeeded = specificationCategory.isSingleChoice && categorySelectedValues?.length > 1 && type !== 'fromRequirements'
    return isNeedToRender ? (
      isNeedMultipleSelect || isHaveMoreValuesThanNeeded ?
        <FormMultiAutocomplete
          key={specificationCategory.id}
          disabled={disabled}
          values={categoryValues}
          onChangeSelect={domain.getComplexMultiSelectFieldHandler(
            {
              selectedIds: selectedSpecificationsIds,
              selectedCategoryId: specificationCategory.id,
              selectedCategoryValues: categoryValues,
              onChange,
              specificationCategory: specificationCategories
            },
          )}
          selected={categorySelectedValues}
          label={
            specificationCategory.isActive
              ? specificationCategory.name || ''
              : `${specificationCategory.name} (неактивен)`
          }
          helperText={specificationCategory.description}
          warningMessage={isHaveMoreValuesThanNeeded ? 'Для этой характеристики можно выбрать только одно значение' : ''}
        /> :
        <FormAutocomplete
          key={specificationCategory.id}
          disabled={disabled}
          values={categoryValues}
          onChangeSelect={(e) => {
            const change = domain.getComplexMultiSelectFieldHandler(
              {
                selectedIds: selectedSpecificationsIds,
                selectedCategoryId: specificationCategory.id,
                selectedCategoryValues: categoryValues,
                onChange,
                specificationCategory: specificationCategories
              },
            )
            change(e ? [e] : [])
          }
          }
          selected={categorySelectedValue || null}
          label={
            specificationCategory.isActive
              ? specificationCategory.name || ''
              : `${specificationCategory.name} (неактивен)`
          }
          helperText={specificationCategory.description}
          variant={'standard'}
          controlPadding='8px'
          selectedDefault={null}
        />
    ) : (
      <div key={specificationCategory.id} />
    );
  },
);
